import React, { useEffect, useRef, useState } from "react";
import "./ArticleUploadModal.css";
import { apiFetch } from "../utils/apifetch";

/* ================= CONFIG ================= */
const MAX_PDF_MB = 10;
const MAX_PDF_BYTES = MAX_PDF_MB * 1024 * 1024;

/* ================= HELPERS ================= */
const extractFilename = (path) => {
  if (!path) return "";
  return path.replace(/\\/g, "/").split("/").pop();
};

export default function ArticleUploadModal({
  mode,
  articleData,
  onClose,
  onArticleUploaded,
}) {
  const isEdit = mode === "edit";

  /* ========== ORIGINAL SNAPSHOT ========== */
  const originalRef = useRef({
    AuthorName: "",
    AuthorInitial: "",
    AuthorTitle: "",
    ShortDescription: "",
  });
  const originalPdfRef = useRef(null);

  /* ========== STATE ========== */
  const [AuthorName, setAuthorName] = useState("");
  const [AuthorInitial, setAuthorInitial] = useState("");
  const [AuthorTitle, setAuthorTitle] = useState("");
  const [ShortDescription, setShortDescription] = useState("");

  const [pdfFile, setPdfFile] = useState(null);
  const [existingPdfName, setExistingPdfName] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* ========== PREFILL (EDIT MODE) ========== */
  useEffect(() => {
    if (isEdit && articleData) {
      setAuthorName(articleData.AuthorName || "");
      setAuthorInitial(articleData.AuthorInitial || "");
      setAuthorTitle(articleData.AuthorTitle || "");
      setShortDescription(articleData.ShortDescription || "");

      originalRef.current = {
        AuthorName: articleData.AuthorName || "",
        AuthorInitial: articleData.AuthorInitial || "",
        AuthorTitle: articleData.AuthorTitle || "",
        ShortDescription: articleData.ShortDescription || "",
      };

      const pdfName =
        articleData.ArticlePdfOriginalName ||
        extractFilename(articleData.ArticlePdfPath);

      originalPdfRef.current = pdfName;
      setExistingPdfName(pdfName);
    }
  }, [isEdit, articleData]);

  /* ========== CHANGE DETECTION ========== */
  const isChanged = () => {
    if (!isEdit) return true;

    if (AuthorName !== originalRef.current.AuthorName) return true;
    if (AuthorInitial !== originalRef.current.AuthorInitial) return true;
    if (AuthorTitle !== originalRef.current.AuthorTitle) return true;
    if (ShortDescription !== originalRef.current.ShortDescription) return true;
    if (pdfFile) return true;

    return false;
  };

  /* ========== SUBMIT ========== */
  const submit = async (e) => {
    e.preventDefault();

    if (!AuthorName.trim() || !ShortDescription.trim()) {
      setError("Required fields missing");
      return;
    }

    /* No-change edit */
    if (isEdit && !isChanged()) {
      onArticleUploaded("nochange");
      onClose();
      return;
    }

    if (!isEdit && !pdfFile) {
      setError("Article PDF is required");
      return;
    }

    const fd = new FormData();
    fd.append("AuthorName", AuthorName.trim());
    fd.append("AuthorInitial", AuthorInitial.trim());
    fd.append("AuthorTitle", AuthorTitle.trim());
    fd.append("ShortDescription", ShortDescription.trim());
    if (pdfFile) fd.append("articlePdf", pdfFile);

    try {
      setLoading(true);
      setError("");

      await apiFetch(
        isEdit ? `/article/${articleData.Id}` : "/article",
        {
          method: isEdit ? "PUT" : "POST",
          body: fd,
        }
      );

      onArticleUploaded("updated");
      onClose();
    } catch {
      setError("Unable to save article");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <h2>{isEdit ? "Edit Article" : "Upload Article"}</h2>

        {error && <div className="error">{error}</div>}

        <form onSubmit={submit}>
          <input
            type="text"
            value={AuthorName}
            onChange={(e) => setAuthorName(e.target.value)}
            placeholder="Author Name"
          />

          <input
            type="text"
            value={AuthorInitial}
            onChange={(e) => setAuthorInitial(e.target.value)}
            placeholder="Author Initial"
          />

          <input
            type="text"
            value={AuthorTitle}
            onChange={(e) => setAuthorTitle(e.target.value)}
            placeholder="Author Title"
          />

          <textarea
            maxLength={255}
            value={ShortDescription}
            onChange={(e) => setShortDescription(e.target.value)}
            placeholder="Article Title"
          />

          {/* ===== EXISTING PDF (EDIT MODE) ===== */}
          {isEdit && existingPdfName && (
            <div className="existing-file">
              Current file: <strong>{existingPdfName}</strong>
            </div>
          )}

          {/* ===== FILE INPUT ===== */}
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => {
              const f = e.target.files[0];
              if (!f) return;

              if (f.size > MAX_PDF_BYTES) {
                setError(`PDF must be ≤ ${MAX_PDF_MB} MB`);
                return;
              }

              /* ✅ Suggestion A: clear old error */
              setError("");
              setPdfFile(f);
            }}
          />

          {/* ✅ Suggestion B: size hint */}
          <small className="file-hint">
            PDF size limit: {MAX_PDF_MB} MB
          </small>

          <div className="modal-actions">
            <button type="submit" disabled={loading}>
              {isEdit ? "Update" : "Upload"}
            </button>
            <button
              type="button"
              className="cancel-btn"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
